'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CurrencyForm, type CurrencyFormRef } from '@/components/partials/currencies'
import { FormSkeleton, ContentError } from '@/components/partials'
import { useCurrency, useUpdateCurrency } from '@/hooks'
import { CreateCurrencyParams, UpdateCurrencyParams } from '@/types/currency'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const UpdateCurrency = ({ id }: PropsType) => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const currencyFormRef = useRef<CurrencyFormRef>(null)

  const { data: currencyData, isLoading, error } = useCurrency(id)
  const { mutate: updateCurrency, isPending } = useUpdateCurrency()

  const handleSubmit = (data: CreateCurrencyParams | UpdateCurrencyParams) => {
    updateCurrency(
      { currencyId: id, params: data },
      {
        onSuccess: async updatedCurrency => {
          setIsRedirecting(true)
          queryClient.setQueryData(['currency', id], updatedCurrency)
          await queryClient.invalidateQueries({ queryKey: ['currencies'] })
          toast.success(t('updated_successfully'))
          router.push(`/currencies/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (currencyFormRef.current) {
        currencyFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating_currency'))
  }

  const handleCancel = () => {
    router.push(`/currencies/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={1} />
  }

  if (error || !currencyData) {
    return <ContentError error={error} listUrl="/currencies" />
  }

  return (
    <CurrencyForm
      ref={currencyFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={currencyData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default UpdateCurrency
